import { focusTarget } from "@/ui/a11y/focusTarget";
import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { fieldFrame, type FieldLabelPosition, type FieldWidth } from "@/ui/components/fieldChrome";
import { usePanelContentBg } from "@/theme/PanelContentBgContext";
import { useTheme } from "@/theme/ThemeProvider";
import {
  DATE_BEFORE_MIN_YEAR_ERROR,
  DATE_IN_FUTURE_ERROR,
  formatIsoDateDE,
  FOUND_ON_MIN_ISO,
  isoDateInputError,
  isStrictIsoDate,
  todayIsoLocal,
} from "@/utils/isoDate";
import DateTimePicker, {
  type DateTimePickerEvent,
} from "@react-native-community/datetimepicker";
import { CaretDownIcon } from "phosphor-react-native";
import { createElement, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from "react-native";

type Props = {
  label: string;
  value: string | null;
  onChange: (iso: string | null) => void;
  required?: boolean;
  optional?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  /** Maximales wählbares Datum (YYYY-MM-DD), z. B. heute. */
  maxIso?: string | null;
  /** Frühestes wählbares Datum (YYYY-MM-DD), z. B. Funddatum. */
  minIso?: string | null;
  /** Meldung, wenn der Tag vor minIso liegt. */
  minIsoMessage?: string;
  /** Meldung, wenn der Tag nach maxIso liegt. */
  maxIsoMessage?: string;
  /** Meldung, wenn der eingegebene Tag kein Kalenderdatum ist. */
  invalidIsoMessage?: string;
  /**
   * false sperrt Speichern: die Eingabe ist neu und ungültig, auch
   * Zwischenwerte beim Tippen des Jahres.
   * Ein unverändert übergebener Altwert vor 1900 zeigt den Fehler, meldet
   * aber true, damit andere Felder gespeichert werden können.
   * DeathClosureFields kann dieselbe Rückmeldung nutzen.
   */
  onValidityChange?: (valid: boolean) => void;
  /** Enter im Web-Feld, z. B. weiter zum Fundort. */
  onSubmitEditing?: () => void;
  /** Steigt, wenn das Web-Feld den Fokus erhalten soll. */
  focusRequest?: number;
  labelPosition?: FieldLabelPosition;
  dense?: boolean;
  width?: FieldWidth;
};

function isoToLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

const LEGACY_STORED_DATE_HINT =
  "Gespeichertes Datum ungültig, bitte korrigieren oder entfernen.";
/** Eine Zeile im Rahmen. Die ausführliche Meldung steht nur im Alert darunter. */
const PARTIAL_DATE_FIELD_TEXT = "Ungültiges Datum";

type DateFieldEvent = {
  key?: string;
  preventDefault?: () => void;
  target?: { value?: string; validity?: { badInput?: boolean } };
  currentTarget?: { value?: string; validity?: { badInput?: boolean } };
};

function eventBadInput(event: DateFieldEvent | undefined): boolean {
  if (!event) return false;
  return Boolean(event.target?.validity?.badInput || event.currentTarget?.validity?.badInput);
}

function eventFieldValue(event: DateFieldEvent | undefined): string | undefined {
  if (!event) return undefined;
  if (event.target && "value" in event.target) return event.target.value;
  if (event.currentTarget && "value" in event.currentTarget) return event.currentTarget.value;
  return undefined;
}

function localDateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Kalendertag inklusive Altwerten vor 1900. Ungültige Texte bleiben leer. */
function calendarIsoOrNull(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed === "" || formatIsoDateDE(trimmed) === "–") return null;
  return trimmed;
}

/**
 * Plattformgerechtes Datumsfeld: Web = input type=date, Native = DateTimePicker.
 * Liefert nur strikte ISO-Daten oder null.
 */
export function FloatingLabelDatePicker({
  label,
  value,
  onChange,
  required = false,
  optional = false,
  allowClear = false,
  placeholder = "Datum wählen…",
  maxIso = null,
  minIso = null,
  minIsoMessage = "Das Datum liegt vor dem frühesten erlaubten Tag.",
  maxIsoMessage = "Das Datum darf nicht in der Zukunft liegen.",
  invalidIsoMessage = "Ungültiges Datum. Bitte ein Kalenderdatum wählen.",
  onValidityChange,
  onSubmitEditing,
  focusRequest = 0,
  labelPosition = "inset",
  dense = false,
  width,
}: Props) {
  const { theme } = useTheme();
  const frame = fieldFrame(theme, { labelPosition, dense, width });
  const panelContentBg = usePanelContentBg();
  const labelBg = panelContentBg ?? theme.color.inputLabelBg;
  const borderColor = theme.color.inputBorder ?? theme.color.border;
  const [nativeOpen, setNativeOpen] = useState(false);
  const [webFocused, setWebFocused] = useState(false);
  const webInputRef = useRef<{
    focus?: (options?: { preventScroll?: boolean; focusVisible?: boolean }) => void;
    value?: string;
  } | null>(null);
  const triggerRef = useRef<View>(null);
  const focusFrame = useRef<number | null>(null);
  const errorDescId = useId();
  useEffect(() => {
    if (!focusRequest) return;
    webInputRef.current?.focus?.();
  }, [focusRequest]);

  useEffect(() => {
    return () => {
      if (focusFrame.current == null || typeof cancelAnimationFrame !== "function") return;
      cancelAnimationFrame(focusFrame.current);
      focusFrame.current = null;
    };
  }, []);

  const hasValue = !!(value && isStrictIsoDate(value) && value.trim() !== "");
  const canClear = optional || allowClear;
  const maxDate = maxIso && isStrictIsoDate(maxIso) && maxIso.trim() !== "" ? maxIso : null;
  const minDate = minIso && isStrictIsoDate(minIso) && minIso.trim() !== "" ? minIso : null;
  const [rejectedInput, setRejectedInput] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [incomplete, setIncomplete] = useState(false);
  const incompleteRef = useRef(false);
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;

  useEffect(() => {
    if (incompleteRef.current) return;
    if (value != null && value.trim() !== "") {
      const err = isoDateInputError(value.trim(), maxDate);
      if (err) {
        setRejectedInput(value.trim());
        setFieldError(err);
        return;
      }
    }
    setRejectedInput(null);
    setFieldError(null);
  }, [value, maxDate]);

  const committed = (value ?? "").trim();
  const pendingEdit = rejectedInput != null && rejectedInput !== committed;
  const legacyUnchanged =
    !pendingEdit && fieldError === DATE_BEFORE_MIN_YEAR_ERROR;
  const blocksSave = incomplete || (fieldError != null && !legacyUnchanged);
  useEffect(() => {
    onValidityChangeRef.current?.(!blocksSave);
  }, [blocksSave]);

  const visibleIso = calendarIsoOrNull(rejectedInput) ?? calendarIsoOrNull(value);
  const storedIso = calendarIsoOrNull(value);
  const storedBeforeMin = storedIso != null && storedIso < FOUND_ON_MIN_ISO;
  const nativeMinIso = minDate && minDate > FOUND_ON_MIN_ISO ? minDate : FOUND_ON_MIN_ISO;

  const messageForInputError = (err: string) =>
    err === DATE_BEFORE_MIN_YEAR_ERROR
      ? err
      : err === DATE_IN_FUTURE_ERROR
        ? maxIsoMessage
        : invalidIsoMessage;

  const rejectIso = (iso: string, message: string) => {
    setRejectedInput(iso);
    setFieldError(message);
  };

  const notePartialDate = () => {
    incompleteRef.current = true;
    setIncomplete(true);
    setRejectedInput(null);
    setFieldError(invalidIsoMessage);
  };

  const releaseEmptyDate = () => {
    incompleteRef.current = false;
    setIncomplete(false);
    setRejectedInput(null);
    setFieldError(null);
    onChange(null);
  };

  const settleDateKey = (event: DateFieldEvent | undefined) => {
    if (eventBadInput(event)) {
      notePartialDate();
      return;
    }
    // Rücktaste auf den letzten Segmentrest feuert kein input. Blur und Enter
    // sehen dann value "" und badInput false. Das ist kein Datum mehr.
    if (incompleteRef.current && eventFieldValue(event) === "") releaseEmptyDate();
  };

  const clearField = () => {
    // Teildatum: der Knoten meldet schon "". React schreibt "" nicht erneut,
    // die getippten Teile bleiben und der nächste Blur setzt den Fehler neu.
    if (incompleteRef.current && webInputRef.current) {
      webInputRef.current.value = "";
    }
    incompleteRef.current = false;
    setIncomplete(false);
    setRejectedInput(null);
    setFieldError(null);
    onChange(null);
    if (Platform.OS === "web") {
      // iPad Safari verwirft focus() auf type=date im selben Tap wie
      // „Datum entfernen“. Der nächste Frame setzt ihn nach.
      focusTarget(webInputRef.current, true);
      if (typeof requestAnimationFrame === "function") {
        const frame = requestAnimationFrame(() => focusTarget(webInputRef.current, true));
        focusFrame.current = frame;
      }
      return;
    }
    focusTarget(triggerRef.current, true);
  };

  const fieldBg = panelContentBg ?? theme.color.surface;
  const webInputStyle = useMemo(
    () =>
      ({
        width: "100%",
        height: frame.minHeight,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor,
        borderRadius: frame.radius,
        paddingLeft: frame.paddingH,
        paddingRight: frame.paddingH,
        paddingTop: frame.paddingTop,
        paddingBottom: frame.paddingV,
        backgroundColor: fieldBg,
        fontSize: 14,
        fontWeight: "600",
        boxSizing: "border-box",
      }) as unknown as ViewStyle,
    [borderColor, fieldBg, frame.minHeight, frame.paddingH, frame.paddingTop, frame.paddingV, frame.radius]
  );

  if (Platform.OS === "web") {
    return (
      <View
        style={[
          { position: "relative" },
          frame.above ? { gap: theme.space.dense } : null,
          frame.widthStyle,
        ]}
      >
        {frame.above ? (
          <Text
            style={{
              color: theme.color.textSecondary,
              fontSize: frame.labelSize,
              fontWeight: "600",
            }}
          >
            {label}
            {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
          </Text>
        ) : (
          <Text
            style={[
              styles.floatingLabel,
              {
                color: theme.color.inputLabelFloatingColor,
                backgroundColor: labelBg,
                fontSize: frame.labelSize,
                zIndex: 1,
              },
            ]}
          >
            {label}
            {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
          </Text>
        )}
        <View style={{ position: "relative" }}>
        {webFocused ? null : (
          <Text
            pointerEvents="none"
            style={{
              position: "absolute",
              left: frame.paddingH,
              right: 36,
              top: frame.paddingTop + 2,
              color: incomplete
                ? theme.color.dangerText
                : hasValue || rejectedInput
                  ? theme.color.text
                  : theme.color.muted,
              fontSize: 14,
              fontWeight: "600",
              zIndex: 1,
            }}
            numberOfLines={1}
            aria-hidden
            accessibilityElementsHidden
            importantForAccessibility="no"
          >
            {incomplete
              ? PARTIAL_DATE_FIELD_TEXT
              : rejectedInput
                ? rejectedInput
                : hasValue
                  ? formatIsoDateDE(value)
                  : placeholder}
          </Text>
        )}
        {createElement("input", {
          ref: (node: { focus?: () => void; value?: string } | null) => {
            webInputRef.current = node;
          },
          type: "date",
          lang: "de-DE",
          value: incomplete ? "" : (rejectedInput ?? (hasValue ? value! : "")),
          max: maxDate ?? undefined,
          onFocus: () => setWebFocused(true),
          onBlur: (event?: DateFieldEvent) => {
            setWebFocused(false);
            settleDateKey(event);
          },
          onKeyDown: (event: DateFieldEvent) => {
            if (event.key === "Enter") settleDateKey(event);
            if (!onSubmitEditing || event.key !== "Enter") return;
            event.preventDefault?.();
            onSubmitEditing();
          },
          onChange: (e: { target: { value: string; validity?: { badInput?: boolean } } }) => {
            const v = e.target.value;
            if (!v) {
              // Ein leerer Monat liefert "" und badInput. Das ist kein geleertes Feld.
              if (e.target.validity?.badInput) {
                notePartialDate();
                return;
              }
              // Komplett leeren, auch nach einem Teildatum: leeres Feld ist kein Datum.
              incompleteRef.current = false;
              setIncomplete(false);
              setRejectedInput(null);
              setFieldError(null);
              onChange(null);
              return;
            }
            incompleteRef.current = false;
            setIncomplete(false);
            const err = isoDateInputError(v, maxDate);
            if (err) {
              rejectIso(v, messageForInputError(err));
              return;
            }
            if (minDate && v < minDate) {
              rejectIso(v, minIsoMessage);
              return;
            }
            setRejectedInput(null);
            setFieldError(null);
            onChange(v);
          },
          style: {
            ...webInputStyle,
            borderColor: fieldError ? theme.color.danger : borderColor,
            color: webFocused ? theme.color.text : "transparent",
            colorScheme: theme.mode === "dark" ? "dark" : "light",
          },
          "data-de-date": "",
          "data-scheme": theme.mode,
          min: minDate ?? undefined,
          "aria-label": hasValue ? `${label}, ${formatIsoDateDE(value)}` : label,
          "aria-invalid": fieldError ? true : undefined,
          "aria-describedby": fieldError ? errorDescId : undefined,
        })}
        </View>
        {storedBeforeMin ? (
          <Text
            accessibilityRole="alert"
            style={{
              marginTop: theme.space.xs,
              color: theme.color.warningText,
              fontSize: theme.fontSize.tiny,
            }}
          >
            {LEGACY_STORED_DATE_HINT}
          </Text>
        ) : null}
        {fieldError ? (
          <View
            nativeID={errorDescId}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
          >
            <Text
              style={{
                marginTop: theme.space.xs,
                color: theme.color.dangerText,
                fontSize: theme.fontSize.tiny,
              }}
            >
              {fieldError}
            </Text>
          </View>
        ) : null}
        {canClear && (hasValue || rejectedInput != null || incomplete) ? (
          <Pressable
            onPress={clearField}
            style={styles.clearBtn}
            accessibilityRole="button"
            accessibilityLabel="Datum entfernen"
          >
            <Text style={{ color: theme.color.primary, fontWeight: "600" }}>
              Datum entfernen
            </Text>
          </Pressable>
        ) : null}
        {!hasValue && !fieldError && webFocused ? (
          <Text
            style={{
              marginTop: theme.space.xs,
              color: theme.color.muted,
              fontSize: 12,
            }}
          >
            {placeholder}
          </Text>
        ) : null}
      </View>
    );
  }

  let pickerIso = todayIsoLocal();
  if (visibleIso && isStrictIsoDate(visibleIso)) {
    pickerIso = visibleIso;
  } else if (storedBeforeMin) {
    pickerIso = nativeMinIso;
  }
  if (pickerIso < nativeMinIso) pickerIso = nativeMinIso;
  if (maxDate && pickerIso > maxDate) pickerIso = maxDate;
  const pickerValue = isoToLocalDate(pickerIso);
  const nativeMinimumDate = isoToLocalDate(nativeMinIso);

  const onNativeChange = (event: DateTimePickerEvent, date?: Date) => {
    if (event.type !== "set") {
      setNativeOpen(false);
      return;
    }
    if (Platform.OS === "android") setNativeOpen(false);
    if (!date) return;
    const iso = localDateToIso(date);
    // Android-OK auf dem Öffnungs-Startwert würde den Altwert sonst still
    // durch 1900-01-01 bzw. minIso ersetzen. Das ist kein gewähltes Datum.
    if (storedBeforeMin && iso === nativeMinIso) return;
    if (iso < FOUND_ON_MIN_ISO) {
      rejectIso(iso, DATE_BEFORE_MIN_YEAR_ERROR);
      return;
    }
    const err = isoDateInputError(iso, maxDate);
    if (err) {
      rejectIso(iso, messageForInputError(err));
      return;
    }
    if (minDate && iso < minDate) {
      rejectIso(iso, minIsoMessage);
      return;
    }
    setRejectedInput(null);
    setFieldError(null);
    onChange(iso);
  };

  return (
    <View
      style={[
        { position: "relative" },
        frame.above ? { gap: theme.space.dense } : null,
        frame.widthStyle,
      ]}
    >
      {frame.above ? (
        <Text
          style={{
            color: theme.color.textSecondary,
            fontSize: frame.labelSize,
            fontWeight: "600",
          }}
        >
          {label}
          {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
        </Text>
      ) : null}
      <Pressable
        ref={triggerRef}
        onPress={() => setNativeOpen(true)}
        style={[
          styles.trigger,
          {
            borderColor,
            backgroundColor: panelContentBg ?? theme.color.surface,
            borderRadius: frame.radius,
            paddingHorizontal: frame.paddingH,
            paddingTop: frame.paddingTop,
            paddingBottom: frame.paddingV,
            height: frame.minHeight,
          },
        ]}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        {frame.above ? null : (
          <Text
            style={[
              styles.floatingLabel,
              {
                color: theme.color.inputLabelFloatingColor,
                backgroundColor: labelBg,
                fontSize: frame.labelSize,
              },
            ]}
          >
            {label}
            {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
          </Text>
        )}
        <Text
          style={[
            styles.triggerText,
            { color: visibleIso ? theme.color.text : theme.color.muted },
          ]}
          numberOfLines={1}
        >
          {visibleIso ? formatIsoDateDE(visibleIso) : placeholder}
        </Text>
        <View style={styles.caretWrap}>
          <CaretDownIcon size={18} color={theme.color.muted} />
        </View>
      </Pressable>

      {canClear && visibleIso ? (
        <Pressable
          onPress={clearField}
          style={styles.clearBtn}
          accessibilityRole="button"
          accessibilityLabel="Datum entfernen"
        >
          <Text style={{ color: theme.color.primary, fontWeight: "600" }}>
            Datum entfernen
          </Text>
        </Pressable>
      ) : null}

      {nativeOpen && Platform.OS === "android" ? (
        <DateTimePicker
          value={pickerValue}
          mode="date"
          display="calendar"
          onChange={onNativeChange}
          maximumDate={maxDate ? isoToLocalDate(maxDate) : undefined}
          minimumDate={nativeMinimumDate}
        />
      ) : null}

      {Platform.OS === "ios" ? (
        <Modal visible={nativeOpen} transparent animationType="fade">
          <View style={styles.modalRoot}>
            <Pressable
              style={styles.modalOverlay}
              onPress={() => setNativeOpen(false)}
            />
            <View
              style={[
                styles.modalSheet,
                { backgroundColor: theme.color.sectionCardBodyBg },
              ]}
            >
              <DateTimePicker
                value={pickerValue}
                mode="date"
                display="spinner"
                onChange={onNativeChange}
                locale="de-DE"
                maximumDate={maxDate ? isoToLocalDate(maxDate) : undefined}
                minimumDate={nativeMinimumDate}
              />
              <View style={{ flexDirection: "row", gap: theme.space.sm }}>
                {canClear ? (
                  <Pressable
                    onPress={() => {
                      clearField();
                      setNativeOpen(false);
                    }}
                    style={{ flex: 1, padding: theme.space.md }}
                  >
                    <Text
                      style={{
                        textAlign: "center",
                        color: theme.color.muted,
                        fontWeight: "600",
                      }}
                    >
                      Kein Datum
                    </Text>
                  </Pressable>
                ) : null}
                <Pressable
                  onPress={() => setNativeOpen(false)}
                  style={{ flex: 1, padding: theme.space.md }}
                >
                  <Text
                    style={{
                      textAlign: "center",
                      color: theme.color.primary,
                      fontWeight: "700",
                    }}
                  >
                    Fertig
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      ) : null}
      {storedBeforeMin ? (
        <Text
          accessibilityLiveRegion="polite"
          style={{
            marginTop: theme.space.xs,
            color: theme.color.warningText,
            fontSize: theme.fontSize.tiny,
          }}
        >
          {LEGACY_STORED_DATE_HINT}
        </Text>
      ) : null}
      {fieldError ? (
        <View accessibilityRole="alert" accessibilityLiveRegion="assertive">
          <Text
            style={{
              marginTop: theme.space.xs,
              color: theme.color.dangerText,
              fontSize: theme.fontSize.tiny,
            }}
          >
            {fieldError}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  trigger: {
    position: "relative",
    borderWidth: 1,
    paddingRight: 40,
    flexDirection: "row",
    alignItems: "center",
  },
  floatingLabel: {
    position: "absolute",
    left: 12,
    top: -8,
    fontSize: 12,
    paddingHorizontal: 6,
    borderRadius: 6,
  },
  triggerText: {
    flex: 1,
    height: 20,
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 20,
  },
  caretWrap: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
  },
  clearBtn: {
    marginTop: 6,
    alignSelf: "flex-start",
    minHeight: HIT_TARGET_MIN,
    justifyContent: "center",
  },
  modalRoot: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.45)",
  },
  modalSheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 12,
  },
});
